import Box from "@mui/material/Box";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";

/**
 * How long a loading state stays blank before its skeleton paints. A read that
 * settles inside it goes from blank straight to content, so it never flashes a
 * skeleton; the cost is a content area left blank for up to this long after
 * the navigation commits.
 *
 * It cannot sit under React's Suspense reveal throttle (`FALLBACK_THROTTLE_MS`,
 * 300 ms in React 19): once a boundary has committed its fallback, React holds
 * the content back until that long after, however early the read settles. A
 * shorter delay paints the skeleton between the two for every fast read.
 */
const SKELETON_DELAY_MS = 300;

const screenReaderOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

export type DelayedFallbackProps = {
  /** What the status region announces once the skeleton paints. */
  announcement: string;
  /** How long to stay blank before painting. */
  delayMs?: number;
  /** The skeleton to paint once the delay has run. */
  children: ReactNode;
};

/**
 * A loading state, held back until it is worth showing — see
 * `SKELETON_DELAY_MS`. Suspense renders a fallback the instant it suspends, so
 * the delay lives here rather than in the boundary.
 *
 * The status region is mounted from the start and only its content arrives
 * with the delay: screen readers announce a change inside a live region they
 * already track, not a region inserted with its content already in it.
 */
export const DelayedFallback = ({
  announcement,
  delayMs = SKELETON_DELAY_MS,
  children,
}: DelayedFallbackProps): ReactElement => {
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="DelayedFallback"
      sx={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}
    >
      {elapsed ? (
        <>
          <Box component="span" sx={screenReaderOnly}>
            {announcement}
          </Box>
          {children}
        </>
      ) : null}
    </Box>
  );
};
